import { create } from 'zustand'

// 1つの操作: undoとredoの両方を持つ
export interface HistoryAction {
  id: string
  label: string               // 表示用「ABC商事 4/10 の金額を 100,000 → 200,000 に変更」
  timestamp: number           // 実行時刻
  undo: () => Promise<void>   // 元に戻す処理
  redo: () => Promise<void>   // やり直す処理
}

interface HistoryState {
  undoStack: HistoryAction[]
  redoStack: HistoryAction[]
  isUndoingOrRedoing: boolean // undo/redo中のフラグ（内部操作を履歴に積まないため）

  push: (action: HistoryAction) => void
  undo: () => Promise<HistoryAction | null>
  redo: () => Promise<HistoryAction | null>
  clear: () => void
  canUndo: () => boolean
  canRedo: () => boolean
}

const MAX_STACK_SIZE = 50

export const useHistoryStore = create<HistoryState>((set, get) => ({
  undoStack: [],
  redoStack: [],
  isUndoingOrRedoing: false,

  push: (action) => {
    // undo/redo中なら積まない（内部操作のため）
    if (get().isUndoingOrRedoing) return

    set((s) => {
      const nextUndo = [...s.undoStack, action]
      // 最大サイズを超えたら古いものを削除
      while (nextUndo.length > MAX_STACK_SIZE) nextUndo.shift()
      // 新しい操作をしたら、redoスタックはクリア（分岐点ができたため）
      return { undoStack: nextUndo, redoStack: [] }
    })
  },

  undo: async () => {
    const { undoStack } = get()
    if (undoStack.length === 0) return null

    const action = undoStack[undoStack.length - 1]
    set({ isUndoingOrRedoing: true })
    try {
      await action.undo()
      // スタック更新: undoからredoへ移動
      set((s) => ({
        undoStack: s.undoStack.slice(0, -1),
        redoStack: [...s.redoStack, action],
      }))
      return action
    } finally {
      set({ isUndoingOrRedoing: false })
    }
  },

  redo: async () => {
    const { redoStack } = get()
    if (redoStack.length === 0) return null

    const action = redoStack[redoStack.length - 1]
    set({ isUndoingOrRedoing: true })
    try {
      await action.redo()
      // スタック更新: redoからundoへ戻す
      set((s) => ({
        redoStack: s.redoStack.slice(0, -1),
        undoStack: [...s.undoStack, action],
      }))
      return action
    } finally {
      set({ isUndoingOrRedoing: false })
    }
  },

  clear: () => set({ undoStack: [], redoStack: [] }),
  canUndo: () => get().undoStack.length > 0,
  canRedo: () => get().redoStack.length > 0,
}))
